import { notifications } from "@mantine/notifications";
import React, { useState } from "react";
import { X } from "lucide-react";
import { CategoryAdminRequestDTO } from "Types/Admin/AdminCategoryType";
import { getImage } from "Utilities/ImageConverter";
import { compressImage } from "Utilities";

interface CategoryFormProps {
  initialValues: CategoryAdminRequestDTO;
  onSubmit: (data: CategoryAdminRequestDTO, imageFile?: File) => void;
  onCancel: () => void;
  mode: "add" | "edit" | "view";
  existingImage?: string;
  onDeleteImage?: () => Promise<void>;
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  mode,
  existingImage,
  onDeleteImage,
}) => {
  const [formData, setFormData] =
    useState<CategoryAdminRequestDTO>(initialValues);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | undefined>(undefined);
  const [isDeletingImage, setIsDeletingImage] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateFileSize = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return false;
    }
    return true;
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const input = e.target;
      const original = input.files![0];
      const file = await compressImage(original, { maxWidth: 1200 }).catch(
        () => original,
      );
      if (!validateFileSize(file)) {
        input.value = "";
        notifications.show({
          message: "Image is too large (max 5MB). Please choose a smaller image.",
          color: "red",
        });
        return;
      }
      setImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = async () => {
    // If we're in add mode (or the user selected a new file), just clear local preview
    // and file state without calling the backend.
    if (!existingImage) {
      setImagePreview(undefined);
      setImageFile(null);
      return;
    }

    if (!onDeleteImage) return;

    try {
      setIsDeletingImage(true);
      await onDeleteImage();
      setImagePreview(undefined);
      setImageFile(null);
    } catch (error) {
      console.error("Failed to delete category image", error);
    } finally {
      setIsDeletingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formData, imageFile || undefined);
    } catch (error) {
      console.error("Submit failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isViewMode = mode === "view";
  const readOnlyProps = isViewMode ? { readOnly: true } : {};

  React.useEffect(() => {
    if (existingImage && (mode === "edit" || mode === "view")) {
  setImagePreview(getImage(existingImage) ?? undefined);

}

  }, [existingImage, mode]);

  return (
    <form onSubmit={handleSubmit} className="font-body tracking-wider">
      <div className="bg-white px-6 py-12 rounded-md shadow-md my-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {/* <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Category Code *
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="categoryCode"
                  value={formData.categoryCode || ""}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md text-sm ${
                    mode !== "add" ? "bg-gray-100" : ""
                  }`}
                  required
                  maxLength={20}
                  placeholder={
                    isGeneratingCode
                      ? "Generating category code..."
                      : "Category code will be auto-generated"
                  }
                  readOnly={true} 
                />
                {isGeneratingCode && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-loader animate-spin text-blue-600"
                    >
                      <path d="M12 2v4" />
                      <path d="m16.2 7.8 2.9-2.9" />
                      <path d="M18 12h4" />
                      <path d="m16.2 16.2 2.9 2.9" />
                      <path d="M12 18v4" />
                      <path d="m4.9 19.1 2.9-2.9" />
                      <path d="M2 12h4" />
                      <path d="m4.9 4.9 2.9 2.9" />
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {mode === "add"
                  ? "Category code is auto-generated by the system"
                  : "Category code cannot be changed"}
              </p>
            </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Category Image
                {/* {!isViewMode && "*"} */}
              </label>
              {isViewMode ? (
                (existingImage || imagePreview) ? (
                  <div className="mt-2">
                    <img
    src={
      imagePreview ||
      getImage(existingImage ?? null) ||
      undefined
    }
                      alt="Category preview"
                      className="max-h-40 object-contain border rounded"
                    />
                  </div>
                ) : (
                  <span className="text-gray-400">No image</span>
                )
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                  />
                  {(imagePreview || (mode === "edit" && existingImage)) && (
                    <div className="mt-2 relative inline-block group">
                      <img
                        src={
                          imagePreview ||
                          getImage(existingImage ?? null) ||
                          undefined
                        }
                        alt="Category preview"
                        className="max-h-40 object-contain border rounded"
                        width="auto"
                        height="auto"
                      />
                      {!isViewMode && (imagePreview || existingImage) && (
                        <button
                          type="button"
                          onClick={handleDeleteImage}
                          disabled={isDeletingImage}
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          title="Delete image"
                        >
                          {isDeletingImage ? (
                            <span className="text-xs">Deleting...</span>
                          ) : (
                            <X size={16} />
                          )}
                        </button>
                      )}
                      {mode === "edit" && existingImage && !imagePreview && (
                        <p className="text-sm text-gray-500 mt-1">
                          Current uploaded image
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {mode !== "add" && (
  <div>
    <label className="block text-sm font-medium text-gray-500 mb-1">
      Category Code
    </label>
    <input
      type="text"
      name="categoryCode"
      value={formData.categoryCode || ""}
      disabled
      readOnly
      className="w-full px-3 py-2 border rounded-md text-sm bg-gray-100 border-gray-300"
    />
    <p className="text-xs text-gray-500 mt-1">
      Category code is system-generated and cannot be modified
    </p>
  </div>
)}

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Category Name*
              </label>
              <input
                type="text"
                name="categoryName"
                value={formData.categoryName}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                {...readOnlyProps}
                required
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Active
              </label>
              {isViewMode ? (
                <div className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 bg-gray-100">
                  {formData.isActive === "Y" ? "Active" : "Inactive"}
                </div>
              ) : (
                <select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:outline-none focus:ring-0"
                  required
                >
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              )}
            </div>
          </div>
        </div>
      </div>

      {mode !== "view" && (
        <div className="flex items-center justify-end gap-3 mt-4">
          <button
            className="px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`px-6 py-2 text-sm font-medium rounded-md flex items-center justify-center gap-2
    ${
      isSubmitting
        ? "bg-blue-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700"
    } text-white`}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
            )}

            {isSubmitting
              ? mode === "add"
                ? "Creating..."
                : "Saving..."
              : mode === "add"
                ? "Add Category"
                : "Save Changes"}
          </button>
        </div>
      )}
    </form>
  );
};
